import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { pillarsAPI } from '../services/api';
import OptimizedImage from './OptimizedImage';
import './PillarHierarchy.css';

interface HierarchyNode {
  _id: string;
  name: string;
  level: number;
  image?: string;
  imageUrl?: string;
  L1pillarImage?: string;
  L1pillarImageUrl?: string;
  article?: string;
  children: HierarchyNode[];
}

interface L1PillarData {
  _id: string;
  name: string;
  image?: string;
  imageUrl?: string;
  L1pillarImage?: string;
  L1pillarImageUrl?: string;
  article?: string;
}

interface HierarchyData {
  l1Pillar: L1PillarData;
  hierarchy: HierarchyNode[];
  totalPillars: number;
}

const PillarHierarchy: React.FC = () => {
  const { l1Name } = useParams<{ l1Name: string }>();
  const navigate = useNavigate();
  const [hierarchyData, setHierarchyData] = useState<HierarchyData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());

  useEffect(() => {
    const fetchHierarchy = async () => {
      if (!l1Name) return;
      
      try {
        setLoading(true);
        const response = await pillarsAPI.getHierarchy(l1Name);
        setHierarchyData(response);
        // Expand the root node by default
        setExpandedNodes(new Set([response.l1Pillar._id]));
      } catch (err) {
        setError('Failed to load hierarchy');
        console.error('Error fetching hierarchy:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchHierarchy();
  }, [l1Name]);

  const toggleNode = (nodeId: string) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  const renderNode = (node: HierarchyNode, depth: number = 0) => {
    const isExpanded = expandedNodes.has(node._id);
    const hasChildren = node.children && node.children.length > 0;

    return (
      <div key={node._id} className="hierarchy-node">
        <div 
          className={`node-content ${hasChildren ? 'has-children' : ''}`}
          style={{ paddingLeft: `${depth * 20}px` }}
        >
          <div className="node-header">
            {hasChildren && (
              <button 
                className={`expand-button ${isExpanded ? 'expanded' : ''}`}
                onClick={() => toggleNode(node._id)}
              >
                {isExpanded ? '▼' : '▶'}
              </button>
            )}
            
            <div className="node-info">
              <div className="node-level">L{node.level}</div>
              <div className="node-name">{node.name}</div>
            </div>
            
                         {node.imageUrl && (
               <div className="node-image-wrapper">
                 <OptimizedImage
                   src={node.imageUrl}
                   alt={node.name}
                   className="hierarchy-node-image"
                 />
               </div>
             )}
          </div>
          
          {node.article && (
            <div className="node-article">
              {node.article.length > 100 
                ? `${node.article.substring(0, 100)}...` 
                : node.article
              }
            </div>
          )}
        </div>
        
        {hasChildren && isExpanded && (
          <div className="node-children">
            {node.children.map(child => renderNode(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="hierarchy-container">
        <div className="loading">Loading hierarchy...</div>
      </div>
    );
  }

  if (error || !hierarchyData) {
    return (
      <div className="hierarchy-container">
        <div className="error">
          {error || 'Failed to load hierarchy'}
          <button onClick={() => navigate('/pillars')} className="back-button">
            Back to Pillars
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="hierarchy-container">
      <div className="hierarchy-header">
        <button onClick={() => navigate('/pillars')} className="back-button">
          ← Back to Pillars
        </button>
        <h1>{hierarchyData.l1Pillar.name} Hierarchy</h1>
        <div className="hierarchy-stats">
          Total Pillars: {hierarchyData.totalPillars}
        </div>
      </div>

      <div className="l1-pillar-section">
        <div className="l1-pillar-card">
                     <div className="l1-pillar-image-wrapper">
             {hierarchyData.l1Pillar.imageUrl && (
               <OptimizedImage
                 src={hierarchyData.l1Pillar.imageUrl}
                 alt={hierarchyData.l1Pillar.name}
                 className="l1-pillar-main-image"
               />
             )}
           </div>
          <div className="l1-pillar-info">
            <h2>{hierarchyData.l1Pillar.name}</h2>
            {hierarchyData.l1Pillar.article && (
              <p className="l1-pillar-article">
                {hierarchyData.l1Pillar.article.length > 200 
                  ? `${hierarchyData.l1Pillar.article.substring(0, 200)}...` 
                  : hierarchyData.l1Pillar.article
                }
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="hierarchy-tree">
        <h3>Pillar Tree</h3>
        {hierarchyData.hierarchy.map(node => renderNode(node))}
      </div>
    </div>
  );
};

export default PillarHierarchy; 